import { computed, inject, Injectable, resource, signal } from '@angular/core';
import {
  createInfiniteScroll,
  keepLastResolvedValue,
  toInfiniteScrollCollectionVm,
  type InfiniteScrollRefreshSource,
} from '@okcargo-apps/layout-ionic';
import { EFreightForCarrierStatus, IFreightBasicForCarrier } from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import {
  ConnectivityService,
  DocumentMetadataQueryService,
  FieldNames,
  FilterCombinator,
  FilterComparator,
  FilterGroupPayload,
  FindOptions,
  FreightCarrierQueryService,
  normalizeFilterCombinators,
  OrderOptions,
  OrderType,
  SearchResult,
  type FilterOptions,
} from '@okcargo-apps/shared-infrastructure';
import { endOfDay, startOfDay } from 'date-fns';
import { AccountService } from '../account';
import { DownloadDocumentService } from '../download-document/download-document.service';
import {
  FREIGHT_CARRIER_DELIVERY_NOTE_TYPE,
  FREIGHT_CARRIER_IN_PROGRESS_EXCLUDED_STATUSES,
  IFreightCarrierDetailedVM,
  IFreightCarrierFilters,
  IFreightCarrierListQuery,
} from './freight-carrier-store.model';

@Injectable({
  providedIn: 'root',
})
export class FreightCarrierStoreService {
  private readonly freightCarrierQueryService = inject(FreightCarrierQueryService);
  private readonly documentMetadataQueryService = inject(DocumentMetadataQueryService);
  private readonly downloadDocumentService = inject(DownloadDocumentService);
  private readonly accountService = inject(AccountService);
  private readonly connectivity = inject(ConnectivityService);

  private readonly PAGE_SIZE = 20;
  private readonly PENDING_DOCUMENTATION_PAGE_SIZE = 5;
  private readonly DETAIL_DOCUMENTS_PAGE_SIZE = 30;

  private readonly _inProgressFilters = signal<IFreightCarrierFilters | null>(null);

  private readonly _finishedFilters = signal<IFreightCarrierFilters | null>(null);

  private readonly _selectedFreightId = signal<string | null>(null);

  // Freights assigned to the company that are not finished yet (in progress).
  // The UI filters are part of the query identity, so any filter change resets
  // the pagination through the collection's linkedSignal.
  private readonly inProgressCollection = createInfiniteScroll<IFreightCarrierListQuery, IFreightBasicForCarrier>({
    query: computed(() => (this.canViewFreights() ? { uiFilters: this._inProgressFilters() } : null)),
    pageSize: this.PAGE_SIZE,
    loadPage: (query, pageIndex, pageSize, abortSignal) =>
      this.freightCarrierQueryService.searchInProgressFreights({
        filters: this.buildInProgressFilterCriteria(query.uiFilters),
        order: this.buildInProgressOrder(),
        pageIndex,
        pageSize,
        abortSignal,
      }),
    getItemKey: (freight) => freight.id,
  });

  // Finished freights assigned to the company.
  private readonly finishedCollection = createInfiniteScroll<IFreightCarrierListQuery, IFreightBasicForCarrier>({
    query: computed(() => (this.canViewFreights() ? { uiFilters: this._finishedFilters() } : null)),
    pageSize: this.PAGE_SIZE,
    loadPage: (query, pageIndex, pageSize, abortSignal) =>
      this.freightCarrierQueryService.searchFinishedFreights({
        filters: this.buildFinishedFilterCriteria(query.uiFilters),
        order: this.buildFinishedOrder(),
        pageIndex,
        pageSize,
        abortSignal,
      }),
    getItemKey: (freight) => freight.id,
  });

  // Dashboard in-progress freights that overlap with today: EARLIEST_STARTING_AT < endOfDay
  // AND LATEST_STARTING_AT > startOfDay, plus the status exclusion filter.
  private readonly inProgressTodayCollection = createInfiniteScroll<IFreightCarrierListQuery, IFreightBasicForCarrier>({
    query: computed(() => (this.canViewFreights() ? { uiFilters: null } : null)),
    pageSize: this.PAGE_SIZE,
    loadPage: (_query, pageIndex, pageSize, abortSignal) =>
      this.freightCarrierQueryService.searchInProgressFreights({
        filters: this.buildInProgressTodayFilterCriteria(),
        order: this.buildInProgressOrder(),
        pageIndex,
        pageSize,
        abortSignal,
      }),
    getItemKey: (freight) => freight.id,
  });

  // Pending documentation lists, split server-side by delivery-note state so that
  // both counters are real backend totals instead of client-side slices of one query.
  // They reuse the finished-freights query (scoped to the FINISHED status) so the
  // same criteria can be reused as filters on the finished page.
  private readonly _freightsWithoutDigitizedNotesResource = resource({
    params: () => (this.canViewFreights() ? true : null),
    loader: async ({ abortSignal, params }): Promise<SearchResult<IFreightBasicForCarrier> | undefined> => {
      if (!params) return undefined;

      // Finished-query scoped to the FINISHED status, equivalent to the former
      // dedicated pending-documentation query; keeps criteria reusable as filters.
      return this.freightCarrierQueryService.searchFinishedFreights({
        filters: this.buildFinishedWithPendingDocumentationCriteria({ withoutDigitizedDeliveryNotes: true }),
        order: [{ field: FieldNames.LATEST_STARTING_AT, type: OrderType.ASC }],
        pageIndex: 0,
        pageSize: this.PENDING_DOCUMENTATION_PAGE_SIZE,
        abortSignal,
      });
    },
  });

  private readonly _paperNotesPendingResource = resource({
    params: () => (this.canViewFreights() ? true : null),
    loader: async ({ abortSignal, params }): Promise<SearchResult<IFreightBasicForCarrier> | undefined> => {
      if (!params) return undefined;

      // Finished-query scoped to the FINISHED status, equivalent to the former
      // dedicated pending-documentation query; keeps criteria reusable as filters.
      return this.freightCarrierQueryService.searchFinishedFreights({
        filters: this.buildFinishedWithPendingDocumentationCriteria({ pendingPaperDeliveryNote: true }),
        order: [{ field: FieldNames.LATEST_STARTING_AT, type: OrderType.ASC }],
        pageIndex: 0,
        pageSize: this.PENDING_DOCUMENTATION_PAGE_SIZE,
        abortSignal,
      });
    },
  });

  private readonly _freightDetailed = resource({
    params: () => this._selectedFreightId(),
    loader: async ({ params: id, abortSignal }) => {
      if (!id) return null;

      const options: FindOptions = {
        id,
        abortSignal,
      };

      return this.freightCarrierQueryService.findFreight(options);
    },
  });

  // Delivery note documents of the selected freight, loaded independently from
  // the detail itself so they can be reloaded on their own after uploads.
  private readonly _deliveryNotesResource = resource({
    params: () => this._selectedFreightId(),
    loader: async ({ params: id, abortSignal }) => {
      if (!id) return [];

      const documents = await this.documentMetadataQueryService.searchDocuments({
        aggregateId: id,
        type: FREIGHT_CARRIER_DELIVERY_NOTE_TYPE,
        pageIndex: 0,
        pageSize: this.DETAIL_DOCUMENTS_PAGE_SIZE,
        abortSignal,
      });

      return documents.map((document) => ({
        ...document,
        url: this.downloadDocumentService.getDownloadDocumentURL(document),
      }));
    },
  });

  // derived state (computed) & exposed state (public readonly)

  /*
   * Keeps the last loaded values when a reload fails (e.g. the connection drops
   * in the middle of a refresh) instead of blanking the view. The selected id is
   * used as the request key, so data from a previous freight is never shown as
   * if it were the selected one.
   */
  private readonly _lastResolvedFreight = keepLastResolvedValue(
    this._freightDetailed.snapshot,
    this._selectedFreightId,
  );

  private readonly _lastResolvedDeliveryNotes = keepLastResolvedValue(
    this._deliveryNotesResource.snapshot,
    this._selectedFreightId,
  );

  public readonly inProgressList = this.inProgressCollection.vm;

  public readonly inProgressTodayList = this.inProgressTodayCollection.vm;

  public readonly finishedList = this.finishedCollection.vm;

  public readonly activeFinishedFilters = this._finishedFilters.asReadonly();

  public readonly freightsWithPendingDocumentation = computed(() =>
    toInfiniteScrollCollectionVm(this._freightsWithoutDigitizedNotesResource),
  );

  public readonly freightsWithoutDeliveryNoteInPaperReceived = computed(() =>
    toInfiniteScrollCollectionVm(this._paperNotesPendingResource),
  );

  public readonly freightDetailed = computed<IFreightCarrierDetailedVM>(() => {
    const error = this._freightDetailed.error();

    return {
      item: this._lastResolvedFreight() ?? null,
      isLoading: this._freightDetailed.isLoading(),
      error: error,
      actionsDisabled: this.connectivity.isOffline(),
    };
  });

  public readonly deliveryNotes = computed(() => this._lastResolvedDeliveryNotes() ?? []);

  // filters commands

  public applyInProgressFilters(filters: IFreightCarrierFilters | null): void {
    this._inProgressFilters.set(filters);
  }

  public clearInProgressFilters(): void {
    this._inProgressFilters.set(null);
  }

  public applyFinishedFilters(filters: IFreightCarrierFilters | null): void {
    this._finishedFilters.set(filters);
  }

  public clearFinishedFilters(): void {
    this._finishedFilters.set(null);
  }

  public clearFinishedFilter(filter: keyof IFreightCarrierFilters): void {
    const current = this._finishedFilters();
    if (!current) return;
    const { [filter]: _removed, ...rest } = current;
    this._finishedFilters.set(Object.keys(rest).length > 0 ? rest : null);
  }

  // QUERIES, imperative calls that force manual state updates

  public setSelectedFreightId(id: string | null): void {
    this._selectedFreightId.set(id);
  }

  public clearSelectedFreightId(): void {
    this._selectedFreightId.set(null);
  }

  public reloadFreightDetail(): void {
    if (!this._selectedFreightId()) return;
    if (this.connectivity.isOffline()) return;
    this._freightDetailed.reload();
    this._deliveryNotesResource.reload();
  }

  public reloadInProgressList(source: InfiniteScrollRefreshSource = 'polling'): void {
    this.inProgressCollection.refresh(source);
  }

  public reloadInProgressTodayList(source: InfiniteScrollRefreshSource = 'polling'): void {
    this.inProgressTodayCollection.refresh(source);
  }

  public reloadFinishedList(source: InfiniteScrollRefreshSource = 'polling'): void {
    this.finishedCollection.refresh(source);
  }

  public reloadPendingDocumentation(): void {
    if (this.connectivity.isOffline()) return;
    this._freightsWithoutDigitizedNotesResource.reload();
    this._paperNotesPendingResource.reload();
  }

  public loadNextInProgressPage(): void {
    this.inProgressCollection.loadNextPage();
  }

  public retryInProgress(): void {
    this.inProgressCollection.retry();
  }

  public loadNextFinishedPage(): void {
    this.finishedCollection.loadNextPage();
  }

  public retryFinished(): void {
    this.finishedCollection.retry();
  }

  public async reloadAfterSaveData(): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, 2000)); // 2 sec

    this.reloadInProgressList('user');
    this.reloadInProgressTodayList('user');
    this.reloadFinishedList('user');
    this.reloadPendingDocumentation();
    this.reloadFreightDetail();
  }

  private canViewFreights(): boolean {
    return this.accountService.hasCapability(ECapabilities.AUTHENTICATED);
  }

  private buildInProgressFilterCriteria(uiFilters: IFreightCarrierFilters | null): FilterOptions[] {
    return normalizeFilterCombinators([
      ...this.buildUserFilterCriteria(uiFilters),
      {
        field: FieldNames.STATUS,
        comparator: FilterComparator.NOT_IN,
        value: FREIGHT_CARRIER_IN_PROGRESS_EXCLUDED_STATUSES,
        combinator: FilterCombinator.AND,
        group: null as unknown as FilterGroupPayload,
      },
    ]);
  }

  private buildInProgressTodayFilterCriteria(): FilterOptions[] {
    const today = new Date();
    return normalizeFilterCombinators([
      {
        field: FieldNames.EARLIEST_STARTING_AT,
        value: endOfDay(today).toISOString(),
        comparator: FilterComparator.LT,
        combinator: FilterCombinator.NONE,
        group: null as unknown as FilterGroupPayload,
      },
      {
        field: FieldNames.FINISHING_AT,
        value: startOfDay(today).toISOString(),
        comparator: FilterComparator.GT,
        combinator: FilterCombinator.AND,
        group: null as unknown as FilterGroupPayload,
      },
      {
        field: FieldNames.STATUS,
        comparator: FilterComparator.NOT_IN,
        value: FREIGHT_CARRIER_IN_PROGRESS_EXCLUDED_STATUSES,
        combinator: FilterCombinator.AND,
        group: null as unknown as FilterGroupPayload,
      },
    ]);
  }

  private buildUserFilterCriteria(uiFilters: IFreightCarrierFilters | null): FilterOptions[] {
    if (!uiFilters) return [];

    const filterCriteria: FilterOptions[] = [];

    // TODO review field name
    if (uiFilters.driverId) {
      filterCriteria.push({
        combinator: FilterCombinator.NONE,
        field: FieldNames.DRIVER_CONTACT_ID,
        comparator: FilterComparator.EQ,
        value: uiFilters.driverId,
        group: null as unknown as FilterGroupPayload,
      });
    }

    // TODO review field name
    if (uiFilters.reference) {
      filterCriteria.push({
        combinator: FilterCombinator.NONE,
        field: FieldNames.SHIPPER_FREIGHT_M2M_REFERENCE,
        comparator: FilterComparator.LIKE,
        value: uiFilters.reference,
        group: null as unknown as FilterGroupPayload,
      });
    }

    // Documentation state filters (without digitized notes / paper note pending) are
    // handled in buildDocumentationFilterCriteria, shared with the pending lists.

    return filterCriteria;
  }

  // Documentation state criteria, shared by the dashboard pending lists and the
  // finished-page filters. Combinators are normalized afterwards (first NONE, rest AND).
  private buildDocumentationFilterCriteria(
    documentation: Pick<IFreightCarrierFilters, 'withoutDigitizedDeliveryNotes' | 'pendingPaperDeliveryNote'>,
  ): FilterOptions[] {
    const criteria: FilterOptions[] = [];

    if (documentation.withoutDigitizedDeliveryNotes) {
      criteria.push({
        field: FieldNames.NUMBER_OF_DELIVERY_NOTES,
        comparator: FilterComparator.EQ,
        value: 0,
        combinator: FilterCombinator.NONE,
        group: null as unknown as FilterGroupPayload,
      });
    }

    if (documentation.pendingPaperDeliveryNote) {
      criteria.push(
        {
          field: FieldNames.DELIVERY_NOTE_IN_PAPER,
          comparator: FilterComparator.IS_TRUE,
          value: true,
          combinator: FilterCombinator.NONE,
          group: null as unknown as FilterGroupPayload,
        },
        {
          field: FieldNames.DELIVERY_NOTE_IN_PAPER_SENT_AT,
          comparator: FilterComparator.IS_NULL,
          value: null,
          combinator: FilterCombinator.AND,
          group: null as unknown as FilterGroupPayload,
        },
      );
    }

    return criteria;
  }

  // Finished-page filters: user criteria plus the documentation state ones. When the
  // documentation state is filtered, the FINISHED status is also required (same condition
  // used to show those freights on the dashboard pending lists).
  private buildFinishedFilterCriteria(uiFilters: IFreightCarrierFilters | null): FilterOptions[] {
    const hasDocumentationCriteria = uiFilters?.withoutDigitizedDeliveryNotes || uiFilters?.pendingPaperDeliveryNote;

    return normalizeFilterCombinators([
      ...this.buildUserFilterCriteria(uiFilters),
      ...this.buildDocumentationFilterCriteria(uiFilters ?? {}),
      ...(hasDocumentationCriteria
        ? [
            {
              field: FieldNames.STATUS,
              comparator: FilterComparator.EQ,
              value: EFreightForCarrierStatus.FINISHED,
              combinator: FilterCombinator.AND,
              group: null as unknown as FilterGroupPayload,
            },
          ]
        : []),
    ]);
  }

  // Dashboard pending lists: documentation criteria scoped to the FINISHED status.
  private buildFinishedWithPendingDocumentationCriteria(
    documentation: Pick<IFreightCarrierFilters, 'withoutDigitizedDeliveryNotes' | 'pendingPaperDeliveryNote'>,
  ): FilterOptions[] {
    return normalizeFilterCombinators([
      ...this.buildDocumentationFilterCriteria(documentation),
      {
        field: FieldNames.STATUS,
        comparator: FilterComparator.EQ,
        value: EFreightForCarrierStatus.FINISHED,
        combinator: FilterCombinator.AND,
        group: null as unknown as FilterGroupPayload,
      },
    ]);
  }

  private buildInProgressOrder(): OrderOptions[] {
    return [
      { field: FieldNames.LATEST_STARTING_AT, type: OrderType.ASC },
      // quickfix: Added a second sort order to avoid pagination issues
      // (if multiple freights have the same STARTING_AT and are on the edge
      // of the pagination then they may appear repeatedly on different pages
      // or not appear on either page)
      { field: FieldNames.ID, type: OrderType.DESC },
    ];
  }

  private buildFinishedOrder(): OrderOptions[] {
    return [
      { field: FieldNames.LATEST_STARTING_AT, type: OrderType.DESC },
      { field: FieldNames.ID, type: OrderType.DESC },
    ];
  }
}
